<template>
  <div class="admin-page">
    <div class="admin-container">
      <div class="admin-header">
        <div class="admin-header__titles">
          <h1 class="admin-title"><i class="fa-solid fa-user-shield"></i> Admin Dashboard</h1>
          <p class="admin-subtitle">User activity, viewers, messages and posting metrics across the platform.</p>
        </div>
        <div class="admin-header__actions">
          <button class="admin-btn admin-btn--ghost" @click="$router.push('/')">
            <i class="fa-solid fa-house"></i> Go to Site
          </button>
        </div>
      </div>

      <!-- Report Tabs -->
      <div class="admin-tabs">
        <button class="admin-tab" :class="{ 'admin-tab--active': activeTab === 'authenticated' }" @click.prevent="activeTab = 'authenticated'">Authenticated</button>
        <button class="admin-tab" :class="{ 'admin-tab--active': activeTab === 'unauthenticated' }" @click.prevent="activeTab = 'unauthenticated'">Unauthenticated</button>
        <button class="admin-tab" :class="{ 'admin-tab--active': activeTab === 'viewersinfo' }" @click.prevent="activeTab = 'viewersinfo'">Viewers Info</button>
        <button class="admin-tab" :class="{ 'admin-tab--active': activeTab === 'contactinfo' }" @click.prevent="activeTab = 'contactinfo'">Message Info</button>
        <button class="admin-tab" :class="{ 'admin-tab--active': activeTab === 'postingscount' }" @click.prevent="activeTab = 'postingscount'">Postings Count</button>
        <button class="admin-tab" :class="{ 'admin-tab--active': activeTab === 'userstats' }" @click.prevent="activeTab = 'userstats'">User Status</button>
        <button class="admin-tab" :class="{ 'admin-tab--active': activeTab === 'clicks' }" @click.prevent="activeTab = 'clicks'">Clicks</button>
      </div>

      <!-- Date Filter Row -->
      <div class="admin-card admin-section">
        <div class="admin-toolbar filters">
          <div class="admin-field">
            <label class="admin-label">From Date</label>
            <DatePicker v-model="fromDate" :max="toDate" placeholder="Any start date" />
          </div>
          <div class="admin-field">
            <label class="admin-label">To Date</label>
            <DatePicker v-model="toDate" :min="fromDate" placeholder="Any end date" />
          </div>
          <div class="admin-field" v-if="activeTab === 'unauthenticated'">
            <label class="admin-label">Page Name</label>
            <SearchableSelect
              v-model="pageNameFilter"
              :options="pageNameSelectOptions"
              :clearable="false"
              placeholder="All Pages"
            />
          </div>
          <div class="admin-field" v-if="activeTab === 'clicks'">
            <label class="admin-label">Click Type</label>
            <SearchableSelect
              v-model="clickType"
              :options="clickTypeOptions"
              :clearable="false"
              placeholder="All Clicks"
            />
          </div>
          <button class="admin-btn admin-btn--primary" @click="fetchData">
            <i class="fa-solid fa-filter"></i> Filter
          </button>
        </div>
      </div>

      <!-- Known-IP exclusion (shared list) — only on the IP-bearing tabs -->
      <IpFilterBox
        v-if="['authenticated', 'unauthenticated', 'viewersinfo', 'clicks'].includes(activeTab)"
        @apply="onIpFilter"
      />

      <!-- Authenticated User Table -->

      <div v-if="activeTab === 'authenticated'" class="admin-section">
        <div class="admin-card__head"><h3 class="admin-card__title">Authenticated Users</h3></div>
        <TableSkeleton v-if="loadingAuthenticated" :columns="4" />
      <div v-else class="admin-table-wrap admin-table-wrap--scroll">
        <table class="admin-table admin-table--rows">
          <thead>
            <tr>
              <th scope="col">User</th>
              <th scope="col">Activity</th>
              <th scope="col">Location</th>
              <th scope="col">Device</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in authenticatedUsers" :key="user.adminreportId">
              <td class="cell-stack">
                <span class="cell-stack__main">
                  <UserLink :user-id="user.userID" :label="user.userName || user.createdBy || 'Unknown'" />
                </span>
              </td>
              <td class="cell-stack">
                <span class="cell-stack__main">{{ user.pageName || "Guest" }}</span>
                <span class="cell-stack__sub is-time">{{ new Date(user.timestamp).toLocaleString() }}</span>
              </td>
              <td class="cell-stack">
                <span class="cell-stack__main">{{ [user.location?.city, user.location?.regionName].filter(Boolean).join(", ") || "N/A" }}</span>
                <span class="cell-stack__sub">{{ user.location?.country || "" }}</span>
              </td>
              <td class="cell-stack">
                <span class="cell-stack__main">{{ user.browser || "N/A" }}</span>
                <span class="cell-stack__sub is-ip">{{ user.ip || "N/A" }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="activeTab === 'unauthenticated'" class="admin-section">
      <div class="admin-card__head"><h3 class="admin-card__title">Unauthenticated Users</h3></div>
        <TableSkeleton v-if="loadingUnauthenticated" :columns="4" />
      <div v-else class="admin-table-wrap admin-table-wrap--scroll">
        <table class="admin-table admin-table--rows">
          <thead>
            <tr>
              <th scope="col">Activity</th>
              <th scope="col">Listing</th>
              <th scope="col">Location</th>
              <th scope="col">Device</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in filteredUnauthenticatedUsers" :key="user.id">
              <td class="cell-stack">
                <span class="cell-stack__main">{{ user.pageName || "N/A" }}</span>
                <span class="cell-stack__sub is-time">{{ new Date(user.timestamp).toLocaleString() }}</span>
              </td>
              <td>
                <a
                  v-if="user.pageName === 'View Ads' && user.serviceSlug"
                  :href="'/view-post/' + user.serviceSlug"
                  target="_blank"
                  rel="noopener"
                >
                  View listing
                </a>
                <span v-else class="admin-muted">N/A</span>
              </td>
              <td class="cell-stack">
                <span class="cell-stack__main">{{ [user.location?.city, user.location?.regionName].filter(Boolean).join(", ") || "N/A" }}</span>
                <span class="cell-stack__sub">{{ user.location?.country || "" }}</span>
              </td>
              <td class="cell-stack">
                <span class="cell-stack__main">{{ user.browser || "N/A" }}</span>
                <span class="cell-stack__sub is-ip">{{ user.ip || "N/A" }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- <div v-if="activeTab === 'viewersinfo'">
      <h3 class="h5 fw-semibold mb-3">Viewers Info</h3>
      <div class="table-responsive">
        <table class="table table-bordered table-striped align-middle">
          <thead class="table-light">
            <tr>
              <th scope="col">Date Time</th>
              <th scope="col">Services Name</th>
              <th scope="col" class="num">Views Count</th>
              <th scope="col">Location</th>
              <th scope="col">Viewer Name</th>
              <th scope="col">Email</th>
              <th scope="col">IP</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in viewersinfo" :key="user._id">
              <td class="cell-time">{{ new Date(user.createdTime).toLocaleString() }}</td>
              <td>{{ user.ServicesName || "N/A" }}</td>
              <td>{{ user.viewCount || "N/A" }}</td>
              <td>{{ user.ServiceLocation || "N/A" }}</td>
              <td>{{ user.viewerUserName || "Guest" }}</td>
              <td>{{ user.Email || "N/A" }}</td>
              <td class="cell-ip">{{ user.ip || "N/A" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div> -->
    <div v-if="activeTab === 'viewersinfo'" class="admin-section">
      <div class="admin-card__head"><h3 class="admin-card__title">Viewers Info</h3></div>

  <TableSkeleton v-if="loadingViewers" :columns="5" />
      <div v-else class="admin-table-wrap admin-table-wrap--scroll">
        <table class="admin-table admin-table--rows">
          <thead>
            <tr>
              <th scope="col">Listing</th>
              <th scope="col" class="num">Views</th>
              <th scope="col">Viewer</th>
              <th scope="col">Location</th>
              <th scope="col">Seen</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in viewersinfo" :key="user._id">
              <td class="cell-stack">
                <span class="cell-stack__main">{{ user.name || "N/A" }}</span>
                <span class="cell-stack__sub">
                  <a
                    v-if="user.serviceSlug"
                    :href="'/view-post/' + user.serviceSlug"
                    target="_blank"
                    rel="noopener"
                  >
                    View listing
                  </a>
                </span>
              </td>
              <td class="num">{{ user.views || "N/A" }}</td>
              <td class="cell-stack">
                <span class="cell-stack__main">
                  <UserLink :user-id="user.viewerUserID" :label="user.viewerName || ''" />
                </span>
                <span class="cell-stack__sub">{{ user.email || "" }}</span>
              </td>
              <td>{{ user.location || "N/A" }}</td>
              <td class="cell-stack">
                <span class="cell-stack__main is-time">{{ new Date(user.createdTime).toLocaleString() }}</span>
                <span class="cell-stack__sub is-ip">{{ user.ip || "N/A" }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="!loadingViewers && viewersinfo.length" class="admin-pagination">
        <span class="admin-pagination__info">
          Showing {{ viewersinfo.length }} of {{ viewersTotal }}
        </span>
        <button
          v-if="viewersinfo.length < viewersTotal"
          class="admin-btn admin-btn--outline admin-btn--sm"
          :disabled="loadingMoreViewers"
          @click="loadMoreViewers"
        >
          <span v-if="loadingMoreViewers" class="admin-spinner" style="width:16px;height:16px;border-width:2px;"></span>
          {{ loadingMoreViewers ? "Loading…" : "Show More" }}
        </button>
      </div>
    </div>

    <div v-if="activeTab === 'contactinfo'" class="admin-section">
      <div class="admin-card__head"><h3 class="admin-card__title">Message Info</h3></div>
        <TableSkeleton v-if="loadingContact" :columns="4" />
      <div v-else class="admin-table-wrap admin-table-wrap--scroll">
        <table class="admin-table admin-table--rows">
          <thead>
            <tr>
              <th scope="col">Conversation</th>
              <th scope="col">Message</th>
              <th scope="col">Sent</th>
              <th scope="col">Link</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="msg in contactinfo" :key="msg._id">
              <td class="cell-stack">
                <span class="cell-stack__main">
                  <UserLink :user-id="String(msg.fromUserId || '')" :label="msg.fromUserName || 'N/A'" />
                </span>
                <span class="cell-stack__sub">
                  to <UserLink :user-id="String(msg.toUserId || '')" :label="msg.toUserName || 'N/A'" />
                </span>
              </td>
              <td class="msg-cell" v-html="renderMessage(msg.message)"></td>
              <td class="cell-stack">
                <span class="cell-stack__main is-time">{{ formatDateTime(msg.sendDate) }}</span>
                <span class="cell-stack__sub is-time">created {{ formatDateTime(msg.createdTime) }}</span>
              </td>
              <td>
                <a
                  v-if="extractLink(msg.message)"
                  :href="extractLink(msg.message)"
                  target="_blank"
                  class="admin-link"
                >
                  View Ad
                </a>
                <span v-else class="admin-muted">N/A</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
<AuthModal
  v-if="showAuthModal"
  :mode="authMode"
  @close="showAuthModal = false"
  @switchMode="authMode = $event"
/>

      <!-- Postings Count by Category and Subcategory (shows all, even zero) -->
      <div v-if="activeTab === 'postingscount'" class="admin-section">
        <div class="admin-card__head"><h3 class="admin-card__title">Postings Count by Category &amp; Subcategory</h3></div>
        <TableSkeleton v-if="loadingPostingsCount" :columns="4" />
        <div v-else class="admin-table-wrap admin-table-wrap--scroll">
          <table class="admin-table admin-table--rows">
            <thead>
              <tr>
                <th>Category</th>
                <th class="num">Count</th>
                <th class="num">Yesterday</th>
                <th class="num">Today</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in postingsCount" :key="row.category + row.subcategory">
                <td class="cell-stack">
                  <span class="cell-stack__main">{{ row.subcategory }}</span>
                  <span class="cell-stack__sub">{{ row.category }}</span>
                </td>
                <td class="num num--bar" :style="{ '--bar': barWidth(row.count, maxPostingsCount) }">{{ row.count }}</td>
                <td class="num">{{ row.yesterday }}</td>
                <td class="num">{{ row.today }}</td>
              </tr>
              <tr class="row-total">
                <td>Total</td>
                <td class="num">{{ totalPostingsCount }}</td>
                <td class="num">{{ totalYesterdayCount }}</td>
                <td class="num">{{ totalTodayCount }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- User Stats Tab -->
      <div v-if="activeTab === 'userstats'" class="admin-section">
        <div v-if="loadingUserStats" class="admin-loading"><div class="admin-spinner"></div></div>

        <div v-else>
          <!-- Side-by-side report cards -->
          <div class="us-grid">
            <!-- Daily Login Activity -->
            <div class="admin-card admin-card--flush">
              <div class="admin-card__head us-card-head">
                <h3 class="admin-card__title"><i class="fa-solid fa-right-to-bracket"></i> Daily Login Activity</h3>
                <span class="admin-badge admin-badge--info">{{ totalLogins }} logins</span>
              </div>
              <div>
                <div v-if="loginTrend.length === 0" class="admin-muted us-empty">No login data found for the selected range.</div>
                <div v-else class="us-scroll">
                  <div v-for="row in loginTrend" :key="row._id" class="us-day">
                    <div class="us-day-row" role="button" @click="toggleLoginDay(row._id)">
                      <div class="us-day-date">
                        <i class="fa-solid" :class="expandedLoginDay === row._id ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                        <span class="ms-2 fw-semibold">{{ row._id }}</span>
                      </div>
                      <div class="us-bar-wrap">
                        <div class="us-bar us-bar--login" :style="{ width: loginBarWidth(row.count) + '%' }"></div>
                      </div>
                      <div class="us-day-count us-count--login">{{ row.count }}</div>
                    </div>
                    <div v-if="expandedLoginDay === row._id" class="us-users">
                      <table class="admin-table admin-table--rows">
                        <thead>
                          <tr>
                            <th>#</th><th>User Name</th><th>User ID</th><th>Login Time</th>
                            <th :title="IP_HINT">IP Address</th>
                            <th class="num" :title="CLICKS_HINT">Clicks</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(u, i) in row.users" :key="i">
                            <td>{{ i + 1 }}</td>
                            <td>
                              <button type="button" class="us-userlink" @click="openUserDetail(u)" title="View full user details">
                                {{ u.userName || u.fullName || u.createdBy || u.userID || 'N/A' }}
                              </button>
                            </td>
                            <td>{{ u.userID || 'N/A' }}</td>
                            <td>{{ u.loginTime ? new Date(u.loginTime).toLocaleString() : 'N/A' }}</td>
                            <td class="us-ip" :title="ipTitle(u)">
                              {{ u.ip || '—' }}
                              <span v-if="extraIpCount(u)" class="us-ip-more">+{{ extraIpCount(u) }}</span>
                            </td>
                            <td class="num">{{ u.clicks || 0 }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- New User Registrations -->
            <div class="admin-card admin-card--flush">
              <div class="admin-card__head us-card-head">
                <h3 class="admin-card__title"><i class="fa-solid fa-user-plus"></i> New User Registrations</h3>
                <span class="admin-badge admin-badge--success">{{ totalSignups }} new</span>
              </div>
              <div>
                <div v-if="signupTrend.length === 0" class="admin-muted us-empty">No registration data found for the selected range.</div>
                <div v-else class="us-scroll">
                  <div v-for="row in signupTrend" :key="row._id" class="us-day">
                    <div class="us-day-row" role="button" @click="toggleSignupDay(row._id)">
                      <div class="us-day-date">
                        <i class="fa-solid" :class="expandedSignupDay === row._id ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                        <span class="ms-2 fw-semibold">{{ row._id }}</span>
                      </div>
                      <div class="us-bar-wrap">
                        <div class="us-bar us-bar--signup" :style="{ width: signupBarWidth(row.count) + '%' }"></div>
                      </div>
                      <div class="us-day-count us-count--signup">{{ row.count }}</div>
                    </div>
                    <div v-if="expandedSignupDay === row._id" class="us-users">
                      <table class="admin-table admin-table--rows">
                        <thead>
                          <tr>
                            <th>#</th><th>User Name</th><th>Email</th><th>Registered At</th>
                            <th :title="IP_HINT">IP Address</th>
                            <th class="num" :title="CLICKS_HINT">Clicks</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(u, i) in row.users" :key="i">
                            <td>{{ i + 1 }}</td>
                            <td>
                              <button type="button" class="us-userlink" @click="openUserDetail(u)" title="View full user details">
                                {{ u.userName || u.fullName || u.createdBy || u.userID || 'N/A' }}
                              </button>
                            </td>
                            <td>{{ u.email || 'N/A' }}</td>
                            <td>{{ u.createdTime ? new Date(u.createdTime).toLocaleString() : 'N/A' }}</td>
                            <td class="us-ip" :title="ipTitle(u)">
                              {{ u.ip || '—' }}
                              <span v-if="extraIpCount(u)" class="us-ip-more">+{{ extraIpCount(u) }}</span>
                            </td>
                            <td class="num">{{ u.clicks || 0 }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Clicks — hero/navbar search, advanced search, and list pagination -->
      <div v-if="activeTab === 'clicks'" class="admin-section">
        <div class="admin-card__head">
          <h3 class="admin-card__title">Clicks</h3>
          <p class="admin-subtitle">
            The hero &amp; navbar search, the advanced-search panel and every list pager — with what was
            searched or which page was opened, plus user, IP and time. Dates are read as IST days.
          </p>
        </div>

        <TableSkeleton v-if="loadingClicks" :columns="5" />

        <template v-else>
          <div class="admin-table-wrap admin-table-wrap--scroll">
            <table class="admin-table admin-table--rows">
              <thead>
                <tr>
                  <th scope="col">Click</th>
                  <th scope="col">Detail</th>
                  <th scope="col">User</th>
                  <th scope="col">Location</th>
                  <th scope="col">Page</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in clicksRows" :key="row._id">
                  <td class="cell-stack">
                    <span class="cell-stack__main">
                      <span class="admin-badge" :class="clickBadgeClass(row)">{{ clickTypeLabel(row) }}</span>
                    </span>
                    <span class="cell-stack__sub is-time">{{ formatDateTime(row.createdAt) }}</span>
                  </td>
                  <td class="cell-stack">
                    <span class="cell-stack__main cell-truncate" :title="clickDetail(row)">{{ clickDetail(row) }}</span>
                    <span class="cell-stack__sub cell-truncate" :title="clickFilters(row)">{{ clickFilters(row) || "—" }}</span>
                  </td>
                  <td class="cell-stack">
                    <span class="cell-stack__main">
                      <UserLink :user-id="row.userId || ''" :label="row.userName || 'Guest'" />
                    </span>
                    <span class="cell-stack__sub">{{ row.role || "guest" }}</span>
                  </td>
                  <td class="cell-stack">
                    <span class="cell-stack__main">{{ [row.city, row.region].filter(Boolean).join(", ") || "—" }}</span>
                    <span class="cell-stack__sub is-ip">{{ row.ip || "N/A" }}</span>
                  </td>
                  <td class="cell-muted">{{ row.path || "—" }}</td>
                </tr>
                <tr v-if="!clicksRows.length"><td colspan="5">No clicks recorded for this filter.</td></tr>
              </tbody>
            </table>
          </div>

          <div v-if="clicksRows.length" class="admin-pagination">
            <span class="admin-pagination__info">Showing {{ clicksRows.length }} of {{ clicksTotal }}</span>
            <button
              v-if="clicksRows.length < clicksTotal"
              class="admin-btn admin-btn--outline admin-btn--sm"
              :disabled="loadingMoreClicks"
              @click="loadMoreClicks"
            >
              <span v-if="loadingMoreClicks" class="admin-spinner" style="width:16px;height:16px;border-width:2px;"></span>
              {{ loadingMoreClicks ? "Loading…" : "Show More" }}
            </button>
          </div>
        </template>
      </div>

      <!-- Full user-activity modal — opened by clicking a user name in the User Status reports -->
      <UserActivityModal
        v-if="activityTarget"
        :user-id="activityTarget.userID"
        :email="activityTarget.email"
        :user-name="activityTarget.userName"
        @close="closeActivity"
      />

      <!-- Legacy shallow user-detail modal (kept, no longer opened) -->
      <div v-if="showUserModal" class="ud-overlay" @click.self="closeUserDetail">
        <div class="ud-modal" role="dialog" aria-modal="true">
          <div class="ud-modal__head">
            <h3 class="ud-modal__title"><i class="fa-solid fa-id-card"></i> User Details</h3>
            <button type="button" class="ud-close" @click="closeUserDetail" aria-label="Close"><i class="fa-solid fa-xmark"></i></button>
          </div>

          <div class="ud-modal__body">
            <div v-if="loadingUserDetail" class="admin-loading"><div class="admin-spinner"></div></div>

            <div v-else-if="userDetailError" class="admin-muted ud-empty">
              <i class="fa-solid fa-circle-exclamation"></i> {{ userDetailError }}
            </div>

            <div v-else-if="userDetail">
              <!-- Identity header -->
              <div class="ud-identity">
                <div class="ud-avatar">{{ udInitials }}</div>
                <div class="ud-identity__meta">
                  <div class="ud-name">
                    <UserLink
                      :user-id="udUser.userID"
                      :label="udUser.fullName || udUser.userName || 'N/A'"
                      title="Open this user's activity profile"
                    />
                  </div>
                  <div class="ud-sub">@{{ udUser.userName || '—' }}</div>
                  <div class="ud-badges">
                    <span class="ud-badge" :class="udUser.role === 'superAdmin' ? 'ud-badge--purple' : (udUser.role === 'adminOrg' ? 'ud-badge--blue' : 'ud-badge--grey')">
                      <i class="fa-solid fa-user-tag"></i> {{ udUser.role || 'user' }}
                    </span>
                    <span class="ud-badge" :class="(udUser.status || 'active') === 'active' ? 'ud-badge--green' : 'ud-badge--red'">
                      {{ udUser.status || 'active' }}
                    </span>
                    <span v-if="udUser.subscriptionPlan" class="ud-badge ud-badge--amber">
                      <i class="fa-solid fa-crown"></i> {{ udUser.subscriptionPlan }}
                    </span>
                    <span class="ud-badge" :class="udUser.activated ? 'ud-badge--green' : 'ud-badge--grey'">
                      <i class="fa-solid" :class="udUser.activated ? 'fa-circle-check' : 'fa-circle-minus'"></i>
                      {{ udUser.activated ? 'activated' : 'not activated' }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- User record — every field from the `user` collection -->
              <div class="ud-section">
                <div class="ud-section__title">User Record — All Fields ({{ userFields.length }})</div>
                <div class="ud-grid">
                  <div v-for="f in userFields" :key="'u-' + f.key" class="ud-field" :class="{ 'ud-field--full': f.full }">
                    <span class="ud-label">{{ f.label }}</span>
                    <span class="ud-value" :class="{ 'ud-mono': f.mono }">{{ f.value }}</span>
                  </div>
                </div>
              </div>

              <!-- Profile (userdetails) — every field from the `userdetails` collection -->
              <div class="ud-section">
                <div class="ud-section__title">Profile Details (userdetails) — All Fields ({{ detailFields.length }})</div>
                <div v-if="!userDetail.details" class="admin-muted ud-empty-inline">
                  <i class="fa-solid fa-circle-info"></i> No extended profile record found for this user.
                </div>
                <div v-else class="ud-grid">
                  <div v-for="f in detailFields" :key="'d-' + f.key" class="ud-field" :class="{ 'ud-field--full': f.full }">
                    <span class="ud-label">{{ f.label }}</span>
                    <span class="ud-value" :class="{ 'ud-mono': f.mono }">{{ f.value }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>
<script setup>
import { ref, onMounted, watch, computed } from "vue";
import axios from "axios";
import { useRoute, useRouter } from "vue-router";
import AuthModal from "../pages/Models/AuthModal.vue";
import DOMPurify from "dompurify";
import IpFilterBox from "@/components/common/IpFilterBox.vue";
import UserLink from "@/components/common/UserLink.vue";
import DatePicker from "@/components/common/DatePicker.vue";
import UserActivityModal from "./Models/UserActivityModal.vue";
import TableSkeleton from "@/components/common/TableSkeleton.vue";
import SearchableSelect from '@/components/pages/Directory/SearchableSelect.vue';
import servicesTypeData from './servicesType.json';
const route = useRoute();
const router = useRouter(); 
const authMode = ref("login");

const fromDate = ref("");
const toDate = ref("");
// When false (default), the getadminreport / getvisitors / viewsreport calls
// strip the shared known-IP list; the IpFilterBox toggles this.
const includeKnownIps = ref(false);
const authenticatedUsers = ref([]);
const unauthenticatedUsers = ref([]);
const pageNameFilter = ref("");

// Page Name dropdown options, derived from the loaded rows so the list always
// reflects the page names actually present (Dashboard, Login Page, View Ads, ...).
const pageNameOptions = computed(() => {
  const names = new Set(
    unauthenticatedUsers.value.map((u) => u.pageName).filter(Boolean)
  );
  return [...names].sort();
});
// The dropdown takes {value,label} pairs; the raw name list above is still used
// elsewhere, so this wraps it rather than replacing it.
const pageNameSelectOptions = computed(() => [
  { value: "", label: "All Pages" },
  ...pageNameOptions.value.map((name) => ({ value: name, label: name })),
]);

const clickTypeOptions = [
  { value: "", label: "All Clicks" },
  { value: "search", label: "Hero / Navbar Search" },
  { value: "advanced", label: "Advanced Search" },
  { value: "pagination", label: "Pagination" },
];

// Rows shown in the table after applying the Page Name filter.
const filteredUnauthenticatedUsers = computed(() => {
  if (!pageNameFilter.value) return unauthenticatedUsers.value;
  return unauthenticatedUsers.value.filter(
    (u) => u.pageName === pageNameFilter.value
  );
});
const viewersinfo = ref([]);
const viewersTotal = ref(0);
const loadingMoreViewers = ref(false);
const VIEWERS_PAGE_SIZE = 50;
const isValid = ref(false);
const showAuthModal = ref(false);
const contactinfo = ref([]);
const loadingAuthenticated = ref(false);
const loadingUnauthenticated = ref(false);
const loadingViewers = ref(false);
const loadingContact = ref(false);

// --- Clicks tab -------------------------------------------------------------
// Backed by /api/clicks-report, which reports only the three actions this tab
// is about: hero/navbar search, the advanced-search panel, and list pagination.
const CLICKS_PAGE_SIZE = 50;
const clickType = ref("");
const clicksRows = ref([]);
const clicksTotal = ref(0);
const clicksPage = ref(1);
const loadingClicks = ref(false);
const loadingMoreClicks = ref(false);
// /api/clicks-report still returns a `stats` block; the tab no longer renders
// summary tiles, so it is simply ignored here.

const CLICK_LABELS = {
  "hero-search": "Search",
  "ai-search": "Search",
  "advanced-search": "Advanced Opened",
  "advanced-apply": "Advanced Applied",
  "page-change": "Pagination",
};
const clickTypeLabel = (row) => CLICK_LABELS[row.action] || row.action || "—";
const clickBadgeClass = (row) => {
  if (row.action === "page-change") return "admin-badge--info";
  if (row.action === "advanced-apply") return "admin-badge--success";
  if (row.action === "advanced-search") return "admin-badge--warning";
  return "admin-badge--purple";
};

// The one-line "what happened" per click. Pagination reads as a move between
// two pages; a search reads as the term (plus the Where box when one was typed).
const clickDetail = (row) => {
  const m = row.meta || {};
  if (row.action === "page-change") {
    return `Page ${m.from ?? "?"} → ${m.to ?? "?"}`;
  }
  if (row.action === "advanced-search") return "Panel opened";
  const term = row.label || "";
  if (row.action === "advanced-apply") return term || "All listings";
  if (term && m.where) return `${term} in ${m.where}`;
  return term || m.where || "—";
};

// The filter context collapsed to one readable line.
const clickFilters = (row) => {
  const m = row.meta || {};
  const parts = [];
  if (row.action === "page-change") {
    if (m.listType) parts.push(m.listType);
    if (m.direction) parts.push(m.direction);
    if (m.totalPages) parts.push(`of ${m.totalPages}`);
    if (m.view) parts.push(m.view);
    return parts.join(" · ");
  }
  if (m.mode) parts.push(m.mode === "ai" ? "AI" : m.mode);
  if (m.source) parts.push(m.source);
  if (m.matchedType) parts.push(m.matchedType);
  if (m.category) parts.push(m.category);
  if (m.subType) parts.push(m.subType);
  if (m.serviceInfo) parts.push(m.serviceInfo);
  if (m.distance) parts.push(`${m.distance}mi`);
  if (m.sortBy) parts.push(`sort: ${m.sortBy}`);
  if (m.withPhotosOnly) parts.push("photos only");
  if (m.country) parts.push(m.country);
  return parts.join(" · ");
};

// `append` powers Show More — it keeps the rows already on screen and adds the
// next page instead of replacing them.
const fetchClicks = async ({ append = false } = {}) => {
  if (append) loadingMoreClicks.value = true;
  else {
    loadingClicks.value = true;
    clicksPage.value = 1;
  }
  try {
    const res = await axios.post("/api/clicks-report", {
      from: fromDate.value || undefined,
      to: toDate.value || undefined,
      clickType: clickType.value || undefined,
      includeKnownIps: includeKnownIps.value,
      page: clicksPage.value,
      limit: CLICKS_PAGE_SIZE,
    }, authConfig());

    const d = res.data || {};
    clicksRows.value = append ? clicksRows.value.concat(d.rows || []) : (d.rows || []);
    clicksTotal.value = (d.pagination && d.pagination.total) || 0;
  } catch (err) {
    if (!handleAuthError(err)) {
      console.error("clicks-report failed:", err);
    }
  } finally {
    loadingClicks.value = false;
    loadingMoreClicks.value = false;
  }
};

const loadMoreClicks = async () => {
  clicksPage.value += 1;
  await fetchClicks({ append: true });
};
// The tab lives in the URL (?tab=) so a drill-down into a user's activity can
// come back to the tab it was launched from — returning always to
// "authenticated" dropped admins on the wrong table.
const TABS = ["authenticated", "unauthenticated", "viewersinfo", "contactinfo", "postingscount", "userstats", "clicks"];
const activeTab = ref(TABS.includes(String(route.query.tab || "")) ? String(route.query.tab) : "authenticated");
const postingsCount = ref([]);
// Largest Count in the table — used only to scale the magnitude bars behind
// the figures, so the tallest bar fills the cell and the rest are relative
// to it. Purely presentational; the numbers themselves are untouched.
const maxPostingsCount = computed(() =>
  Math.max(1, ...postingsCount.value.map((r) => Number(r.count) || 0))
);
const barWidth = (value, max) => `${Math.round((Number(value) || 0) / max * 100)}%`;
const loadingPostingsCount = ref(false);

const totalPostingsCount = ref(0);
const totalTodayCount = ref(0);
const totalYesterdayCount = ref(0);

const loginTrend = ref([]);
const signupTrend = ref([]);

// The IP and click count on each user row are NOT from userlog/user — that
// record holds neither. The backend recovers them from the tracked-activity
// collections for the same calendar day, so a user who never triggered a
// tracked event that day shows "—" and 0. See /userstats/login-trend.
const IP_HINT = "IP this user was seen on that day (from tracked activity — userlog stores no IP)";
const CLICKS_HINT = "Tracked clicks this user made that day: searches, navbar, pagination and listing actions. Page views are not counted.";
// A user can hit the site from more than one IP in a day; show the first and
// hang the rest off a +N badge / tooltip rather than widening the column.
const extraIpCount = (u) => Math.max(0, (u?.ips?.length || 0) - 1);
const ipTitle = (u) => (u?.ips?.length ? u.ips.join(", ") : "");
const loadingUserStats = ref(false);
const expandedLoginDay = ref(null);
const expandedSignupDay = ref(null);

// --- Full user-activity modal (clicking a user name in the User Status reports) ---
// activityTarget holds { userID, email, userName } for the clicked user, or null.
const activityTarget = ref(null);
const closeActivity = () => { activityTarget.value = null; };

// --- Legacy shallow user detail modal (kept for reference, no longer opened) ---
const showUserModal = ref(false);
const loadingUserDetail = ref(false);
const userDetail = ref(null);       // { user, details }
const userDetailError = ref("");

// These admin report endpoints are gated server-side with verifyToken +
// requireAdmin, so every call must carry the JWT. (verifyToken() above only
// confirms the token is valid — the role check happens on the backend.)
const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("auth-token")}` },
  withCredentials: true,
});

// Surface auth failures from the protected endpoints: 401 → re-login,
// 403 → not an admin. Returns true if it handled an auth error.
const handleAuthError = (err) => {
  const status = err?.response?.status;
  if (status === 401) {
    showAuthModal.value = true;
    authMode.value = "login";
    return true;
  }
  if (status === 403) {
    alert("You don't have permission to view this report (admin access required).");
    return true;
  }
  return false;
};

const toggleLoginDay = (date) => {
  expandedLoginDay.value = expandedLoginDay.value === date ? null : date;
};
const toggleSignupDay = (date) => {
  expandedSignupDay.value = expandedSignupDay.value === date ? null : date;
};

// Convenience accessors so the template stays readable.
const udUser = computed(() => userDetail.value?.user || {});
const udDetails = computed(() => userDetail.value?.details || {});
const udInitials = computed(() => {
  const u = udUser.value;
  const src = u.fullName || u.userName || u.email || "";
  const parts = src.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (src.slice(0, 2) || "?").toUpperCase();
});

// ---- Generic full-record rendering ----------------------------------------
// The admin endpoint returns the complete user / userdetails documents (only the
// password hash is stripped server-side), so we render EVERY key rather than a
// curated subset. Sensitive/noise keys are hidden here as a second guard.
const UD_HIDDEN_KEYS = new Set(["password", "passwordHash", "hash", "salt", "__v"]);
const UD_DATE_KEY_RE = /(time|date|_at|At|expiry|expires|lastlogin|loginat)/i;
const UD_ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

const udHumanizeKey = (key) => {
  if (!key) return "";
  if (key === "_id") return "Mongo _id";
  return key
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bId\b/g, "ID")
    .replace(/\bUrl\b/g, "URL")
    .trim();
};

const udFormatValue = (key, val) => {
  if (val === null || val === undefined || val === "") return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (Array.isArray(val)) return val.length ? val.map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v))).join(", ") : "—";
  if (typeof val === "object") {
    try { return JSON.stringify(val); } catch (e) { return String(val); }
  }
  if (typeof val === "string" && UD_ISO_DATE_RE.test(val)) return formatDateTime(val);
  if (UD_DATE_KEY_RE.test(key) && (typeof val === "number" || UD_ISO_DATE_RE.test(String(val)))) {
    const d = new Date(val);
    if (!isNaN(d.getTime())) return d.toLocaleString();
  }
  return String(val);
};

const udToFields = (obj) => {
  if (!obj || typeof obj !== "object") return [];
  return Object.keys(obj)
    .filter((k) => !UD_HIDDEN_KEYS.has(k))
    .map((k) => {
      const display = udFormatValue(k, obj[k]);
      const mono = k === "_id" || /id$/i.test(k);
      const full = display.length > 60 || (obj[k] && typeof obj[k] === "object");
      return { key: k, label: udHumanizeKey(k), value: display, mono, full };
    });
};

const userFields = computed(() => udToFields(udUser.value));
const detailFields = computed(() => udToFields(udDetails.value));

const closeUserDetail = () => {
  showUserModal.value = false;
  userDetail.value = null;
  userDetailError.value = "";
};

// Open the full user-activity modal for a report row. The row carries userID
// for login rows and (now) signup rows; email/userName are passed as fallbacks
// so older rows still resolve server-side. The modal itself loads the detail +
// login/page-view/post/message activity.
const openUserDetail = (u) => {
  activityTarget.value = {
    userID: u.userID || "",
    email: u.email || "",
    userName: u.userName || "",
  };
};

// --- User Stats summary helpers ---
const sumCount = (rows) => rows.reduce((t, r) => t + (r.count || 0), 0);
const maxCount = (rows) => rows.reduce((m, r) => Math.max(m, r.count || 0), 0);

const totalLogins = computed(() => sumCount(loginTrend.value));
const totalSignups = computed(() => sumCount(signupTrend.value));
const maxLoginCount = computed(() => maxCount(loginTrend.value));
const maxSignupCount = computed(() => maxCount(signupTrend.value));
const avgLoginsPerDay = computed(() =>
  loginTrend.value.length ? Math.round(totalLogins.value / loginTrend.value.length) : 0
);
const avgSignupsPerDay = computed(() =>
  signupTrend.value.length ? Math.round(totalSignups.value / signupTrend.value.length) : 0
);
const peakLoginDay = computed(() =>
  loginTrend.value.reduce((best, r) => ((r.count || 0) > (best?.count || 0) ? r : best), null)
);
const peakSignupDay = computed(() =>
  signupTrend.value.reduce((best, r) => ((r.count || 0) > (best?.count || 0) ? r : best), null)
);
// width % of the inline bar for a given count relative to its column max
const loginBarWidth = (count) =>
  maxLoginCount.value ? Math.max(6, Math.round((count / maxLoginCount.value) * 100)) : 0;
const signupBarWidth = (count) =>
  maxSignupCount.value ? Math.max(6, Math.round((count / maxSignupCount.value) * 100)) : 0;

const fetchUserStats = async () => {
  loadingUserStats.value = true;
  expandedLoginDay.value = null;
  expandedSignupDay.value = null;
  try {
    const [loginRes, signupRes] = await Promise.all([
      axios.post("/api/userstats/login-trend", { from: fromDate.value, to: toDate.value }, authConfig()),
      axios.post("/api/userstats/signup-trend", { from: fromDate.value, to: toDate.value }, authConfig()),
    ]);
    loginTrend.value = loginRes.data || [];
    signupTrend.value = signupRes.data || [];
  } catch (err) {
    if (handleAuthError(err)) return;
    console.error("Error fetching user stats:", err);
  } finally {
    loadingUserStats.value = false;
  }
};

// Load the next 50-row batch of viewer records and append to the table.
const loadMoreViewers = async () => {
  if (loadingMoreViewers.value) return;
  const isValid = await verifyToken();
  if (!isValid) return;
  try {
    loadingMoreViewers.value = true;
    const res = await axios.post("/api/viewsreport", {
      from: fromDate.value || undefined,
      to: toDate.value || undefined,
      includeKnownIps: includeKnownIps.value,
      skip: viewersinfo.value.length,
      limit: VIEWERS_PAGE_SIZE,
    }, authConfig());
    viewersinfo.value = viewersinfo.value.concat(res.data.items || []);
    viewersTotal.value = res.data.total || viewersTotal.value;
  } catch (error) {
    if (handleAuthError(error)) return;
    console.error("Error loading more viewers:", error);
  } finally {
    loadingMoreViewers.value = false;
  }
};

// Fired by IpFilterBox after saving the shared list or toggling the exclude
// switch — re-fetch the current tab with the new setting.
const onIpFilter = ({ includeKnownIps: include }) => {
  includeKnownIps.value = include;
  fetchData();
};

const fetchData = async () => {
    const isValid = await verifyToken();
  if (!isValid) return;
  try {
    if (activeTab.value === "authenticated") {
        loadingAuthenticated.value = true;
      const res = await axios.post("/api/getadminreport", {
        from: fromDate.value,
        to: toDate.value,
        includeKnownIps: includeKnownIps.value,
      }, authConfig());

      authenticatedUsers.value = res.data.sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
      );
      loadingAuthenticated.value = false;
   
    } else if (activeTab.value === "unauthenticated") {
         loadingUnauthenticated.value = true;
      const res = await axios.post("/api/getvisitors", {
        from: fromDate.value,
        to: toDate.value,
        includeKnownIps: includeKnownIps.value,
      }, authConfig());
      unauthenticatedUsers.value = res.data.sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
      );
   
      
      loadingUnauthenticated.value = false;
    } else if (activeTab.value === "viewersinfo") {
       loadingViewers.value = true;
      // Read viewer records straight from viewsinfo (fast, indexed) so we show
      // every viewer — not just views on the 50 most-recently-updated listings.
      // Fetch the first page only (50 rows); "Show More" loads the rest in
      // 50-row batches so the browser never renders thousands of rows at once.
      const customResponse = await axios.post("/api/viewsreport", {
        from: fromDate.value || undefined,
        to: toDate.value || undefined,
        includeKnownIps: includeKnownIps.value,
        skip: 0,
        limit: VIEWERS_PAGE_SIZE,
      }, authConfig());
      // Server already sorts by createdTime desc and paginates, so just take
      // the page as-is (re-sorting here would only reorder the current batch).
      viewersinfo.value = customResponse.data.items || [];
      viewersTotal.value = customResponse.data.total || 0;

       loadingViewers.value = false;
    }

else if (activeTab.value === "contactinfo") {
  loadingContact.value = true;

  try {
    const [msgRes, userRes] = await Promise.all([
      axios.post("/api/messagereport", { from: fromDate.value || undefined, to: toDate.value || undefined }, authConfig()),
      axios.post("/api/getallusers", {}, authConfig()),
    ]);

    const messages = msgRes.data.items || [];
    const users = userRes.data.items || userRes.data || [];

    console.log("messages list", messages);
    console.log("users list", users);

    // Build user lookup table
    const userMap = {};
    users.forEach((u) => {
      userMap[String(u.userID)] = u.userName || u.fullName || "Unknown";
    });

    // Sort messages newest first
    messages.sort(
      (a, b) => new Date(b.createdTime) - new Date(a.createdTime)
    );

    // Build directional link map
    const linkMap = {};
    messages.forEach((msg) => {
      const key = `${msg.fromUserId}-${msg.toUserId}`;
      const link = extractLink(msg.message);

      if (link && !linkMap[key]) {
        linkMap[key] = link;
      }
    });

    // Final formatted contact info
    contactinfo.value = messages.map((msg) => {
      const key = `${msg.fromUserId}-${msg.toUserId}`;

      return {
        ...msg,
        fromUserName: userMap[String(msg.fromUserId)] || "N/A",
        toUserName: userMap[String(msg.toUserId)] || "N/A",
        message: msg.message || "N/A",
        sharedLink: linkMap[key] || extractLink(msg.message) || null,
      };
    });

    console.log("Contact Info with Directional Shared Links:", contactinfo.value);
    loadingContact.value = false;

  } catch (error) {
    loadingContact.value = false;
    if (handleAuthError(error)) return;
    console.error("Error loading contact info:", error);
  }


// } else if (activeTab.value === "postingscount") {


//   try {
//     loadingPostingsCount.value = true;

//     // --- TOTAL COUNTS (summary API) ---
//     const summaryRes = await axios.post("/api/servicescountsummary", {
//       from: fromDate.value,
//       to: toDate.value
//     });
//     const summary = summaryRes.data.items || [];
//     const totalMap = {};
//     summary.forEach(r => {
//       const key = `${r.category}|||${r.subcategory}`;
//       totalMap[key] = r.count;
//     });

//     // --- TODAY & YESTERDAY COUNTS (rolling 24h) ---
//     const res = await axios.post("/api/servicestodayyesterdaycount", {
//       from: fromDate.value,
//       to: toDate.value
//     });
//     const rows = res.data.items || [];
//     // Merge today/yesterday with total
//     const postingsMap = {};
//     rows.forEach(row => {
//       const key = `${row.category}|||${row.subcategory}`;
//       postingsMap[key] = row;
//     });
//     // Ensure all categories/subcategories from totalMap are present
//     const allRows = [];
//     Object.keys(totalMap).forEach(key => {
//       const [cat, subcat] = key.split("|||");
//       const row = postingsMap[key] || { category: cat, subcategory: subcat, today: 0, yesterday: 0 };
//       row.count = totalMap[key] || 0;
//       allRows.push(row);
//     });
//     postingsCount.value = allRows;
//     totalPostingsCount.value = allRows.reduce((sum, r) => sum + (r.count || 0), 0);
//     totalTodayCount.value = allRows.reduce((sum, r) => sum + (r.today || 0), 0);
//     totalYesterdayCount.value = allRows.reduce((sum, r) => sum + (r.yesterday || 0), 0);
//     loadingPostingsCount.value = false;
//   } catch (err) {
//     console.error(err);
//     loadingPostingsCount.value = false;
//   }
// }

  }  else if (activeTab.value === "postingscount") {
      try {
        loadingPostingsCount.value = true;

        // --- TOTAL COUNTS (summary API) ---
        const summaryRes = await axios.post("/api/servicescountsummary", {
          from: fromDate.value,
          to: toDate.value
        }, authConfig());
        const summary = summaryRes.data.items || [];
        const totalMap = {};
        summary.forEach(r => {
          const key = `${r.category}|||${r.subcategory}`;
          totalMap[key] = r.count;
        });

        // --- Fetch all postings for date range ---
        const allPostingsRes = await axios.post("/api/servicescustom", {
          from: fromDate.value,
          to: toDate.value,
          limit: 10000, // adjust as needed for your data size
          // We only bucket by createdTime/serviceType here, so skip the costly
          // viewsinfo + serviceimage joins AND fetch only those fields (lean).
          // Drops this call from ~19MB/3s to ~1.7MB/<1s.
          lean: true,
          includeViews: false,
          includeImages: false
        });
        const allPostings = allPostingsRes.data.items || [];

        // Calculate today/yesterday boundaries in UTC.
        // createdTime is stored as an ISO-8601 UTC string, so the report must
        // bucket by the UTC calendar day. Building midnight from local time
        // (the admin's browser runs in IST = UTC+5:30) shifted every posting
        // created in the 18:30–24:00 UTC window into the next day, so "today"
        // and "yesterday" disagreed with the stored createdTime date.
        const now = new Date();
        const todayMidnight = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
        const yesterdayMidnight = new Date(todayMidnight.getTime() - 24 * 60 * 60 * 1000);
        const tomorrowMidnight = new Date(todayMidnight.getTime() + 24 * 60 * 60 * 1000);

        // Helper: parse createdTime as Date
        function parseDate(val) {
          if (!val) return null;
          if (val instanceof Date) return val;
          const d = new Date(val);
          return isNaN(d.getTime()) ? null : d;
        }

        // Build today/yesterday counts by category/subcategory
        const todayMap = {};
        const yesterdayMap = {};
        allPostings.forEach(post => {
          const cat = post.serviceType1 || "Unknown";
          const subcat = post.serviceType2 || "Unknown";
          const key = `${cat}|||${subcat}`;
          const created = parseDate(post.createdTime);
          if (!created) return;
          if (created >= todayMidnight && created < tomorrowMidnight) {
            todayMap[key] = (todayMap[key] || 0) + 1;
          } else if (created >= yesterdayMidnight && created < todayMidnight) {
            yesterdayMap[key] = (yesterdayMap[key] || 0) + 1;
          }
        });

        // Merge all data
        const allRows = [];
        Object.keys(totalMap).forEach(key => {
          const [cat, subcat] = key.split("|||");
          allRows.push({
            category: cat,
            subcategory: subcat,
            count: totalMap[key] || 0,
            today: todayMap[key] || 0,
            yesterday: yesterdayMap[key] || 0
          });
        });
        postingsCount.value = allRows;
        totalPostingsCount.value = allRows.reduce((sum, r) => sum + (r.count || 0), 0);
        totalTodayCount.value = allRows.reduce((sum, r) => sum + (r.today || 0), 0);
        totalYesterdayCount.value = allRows.reduce((sum, r) => sum + (r.yesterday || 0), 0);
        loadingPostingsCount.value = false;
      } catch (err) {
        loadingPostingsCount.value = false;
        if (handleAuthError(err)) return;
        console.error(err);
      }
    } else if (activeTab.value === "userstats") {
      await fetchUserStats();
    } else if (activeTab.value === "clicks") {
      await fetchClicks();
    }

  } catch (err) {
    loadingAuthenticated.value = false;
    loadingUnauthenticated.value = false;
    loadingViewers.value = false;
    if (handleAuthError(err)) return;
    console.error(err);
    alert("Error fetching data. Please try again.");
  }
};


const verifyToken = async () => {
  try {
    const token = localStorage.getItem("auth-token");
  

    if (!token) {
      isValid.value = false;
      showAuthModal.value = true;
      authMode.value = "login";
      return false;
    }

    await axios.post("/api/verify-token", {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: true,
    });

    isValid.value = true;
    return true;

  } catch (err) {
    isValid.value = false;
    showAuthModal.value = true;
    authMode.value = "login";
    return false;
  }
};


// Messages store their "View Ad" link in three shapes depending on when they
// were sent: legacy raw anchors with a RELATIVE href (`view-post/<slug>`),
// newer markdown `[View Ad](<absolute url>)`, or a bare URL. Resolve whatever
// we find against the current origin so the admin always gets a working link.
const toAbsoluteUrl = (href) => {
  if (!href) return null;
  const url = String(href).trim();
  if (/^https?:\/\//i.test(url)) return url;
  // Anything carrying another scheme (javascript:, data:, ...) is dropped: the
  // Link column binds this straight into :href, which Vue does NOT sanitise.
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return null;
  if (url.startsWith("//")) return window.location.protocol + url;
  return `${window.location.origin}/${url.replace(/^\/+/, "")}`;
};

const extractLink = (msg) => {
  if (!msg) return null;

  // 1️⃣ Anchor tag href — do NOT require a protocol here: the legacy chat
  // messages stored `href="view-post/<slug>"`, which the old protocol-only
  // regex missed, leaving the Link column stuck on "N/A".
  let match = msg.match(/href=["']([^"']+)["']/i);
  if (match) return toAbsoluteUrl(match[1]);

  // 2️⃣ Markdown-style [text](url) used by the current chat composer
  match = msg.match(/\[[^\]]*\]\(([^\s)]+)\)/);
  if (match) return toAbsoluteUrl(match[1]);

  // 3️⃣ Fallback: plain URL
  match = msg.match(/https?:\/\/[^\s)]+/);
  return match ? match[0] : null;
};

const escapeHtml = (str) =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

// Render a chat message for the Message Info table. The stored text may contain
// a raw <a> tag, which `{{ }}` would print as visible markup — so pull the
// anchors (and markdown links) out first, escape everything else so no other
// user-supplied markup can execute, then re-emit the links as safe absolute
// anchors and sanitise the result.
const renderMessage = (raw) => {
  if (!raw) return "N/A";

  const links = [];
  const stash = (href, text) =>
    `\u0000LINK${links.push({ href: toAbsoluteUrl(href), text }) - 1}\u0000`;

  let out = String(raw)
    .replace(/<a\b[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi, (m, href, text) =>
      stash(href, text.replace(/<[^>]*>/g, ""))
    )
    .replace(/\[([^\]]+)\]\(([^\s)]+)\)/g, (m, text, href) => stash(href, text))
    .replace(/(^|[\s>])(https?:\/\/[^\s<]+)/g, (m, pre, url) => pre + stash(url, url));

  out = escapeHtml(out).replace(/\n/g, "<br>");

  out = out.replace(/\u0000LINK(\d+)\u0000/g, (m, i) => {
    const link = links[Number(i)];
    if (!link || !link.href) return escapeHtml(link ? link.text : "");
    return `<a href="${escapeHtml(link.href)}" target="_blank" rel="noopener noreferrer" class="admin-link">${escapeHtml(link.text || "View Ad")}</a>`;
  });

  return DOMPurify.sanitize(out, {
    ALLOWED_TAGS: ["a", "br"],
    ALLOWED_ATTR: ["href", "target", "rel", "class"],
  });
};



const formatDateTime = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  return date.toLocaleString();
};



const goBack = () => {
  router.push('/');  
};
// Keep ?tab= in step with the visible tab. replace(), not push(), so flicking
// between tabs doesn't stack history entries. The default tab stays out of the
// URL to keep it clean.
watch(activeTab, (tab) => {
  const next = { ...route.query };
  if (tab && tab !== "authenticated") next.tab = tab;
  else delete next.tab;
  router.replace({ query: next }).catch(() => {});
});

// The Clicks type dropdown re-queries immediately — it is a server-side filter,
// not a client-side one, so it cannot just narrow the rows already loaded.
watch(clickType, () => {
  if (activeTab.value === "clicks") fetchData();
});

onMounted(async () => {
  const valid = await verifyToken();
  if (valid) {
    fetchData();
  }
});



// watch(activeTab, () => {
//   fetchData();
// });
watch(activeTab, async () => {
  const valid = await verifyToken();
  if (valid) {
    fetchData();
  }
});


</script>

<style scoped>
/* Page-specific layout only — shared look comes from admin-theme.css */
.filters { align-items: flex-end; }

/* Clicks tab */
.clicks-filters {
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Postings Count total row */
.admin-table .row-total td { font-weight: 600; color: var(--g-text); }

/* ---- User Stats report cards ---- */
.us-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 14px;
}
.us-card-head { padding: 12px 16px; border-bottom: 1px solid var(--g-border); margin-bottom: 0; }
.us-card-head .admin-card__title { display: flex; align-items: center; gap: 8px; }
.us-card-head .admin-card__title i { color: var(--g-blue); }
.us-empty { padding: 12px 16px; }
.us-scroll {
  max-height: 460px;
  overflow-y: auto;
}
.us-day { border-bottom: 1px solid var(--g-divider); }
.us-day-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 14px;
  cursor: pointer;
  transition: background 0.12s ease;
}
.us-day-row:hover {
  background: var(--g-hover);
}
.us-day-date {
  flex: 0 0 124px;
  font-size: 13px;
  color: var(--g-text);
  white-space: nowrap;
}
.us-day-date i { color: var(--g-text-secondary); }
.us-bar-wrap {
  flex: 1 1 auto;
  background: var(--g-surface-2);
  border-radius: var(--g-radius-sm);
  height: 10px;
  overflow: hidden;
}
.us-bar {
  height: 100%;
  border-radius: var(--g-radius-sm);
  transition: width 0.3s ease;
}
.us-bar--login { background: var(--g-blue); }
.us-bar--signup { background: var(--g-green); }
.us-day-count {
  flex: 0 0 42px;
  text-align: right;
  font-weight: 700;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
}
.us-count--login { color: var(--g-blue); }
.us-count--signup { color: var(--g-green); }
.us-users {
  background: var(--g-surface-2);
  padding: 4px 8px 8px;
  /* The two report cards sit side by side, so the IP + Clicks columns can
     overflow on a narrow card — scroll the table, not the page. */
  overflow-x: auto;
}
.us-ip {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.us-ip-more {
  margin-left: 4px;
  padding: 0 4px;
  border-radius: var(--g-radius-sm);
  background: var(--g-hover);
  color: var(--g-text-secondary);
  font-size: 11px;
  font-weight: 600;
}
.ms-2 { margin-left: 8px; }
.fw-semibold { font-weight: 600; }

/* Clickable user name in the report tables */
.us-userlink {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--g-blue);
  font-weight: 600;
  cursor: pointer;
  text-align: left;
}
.us-userlink:hover { text-decoration: underline; }

/* ---- User Detail modal ---- */
.ud-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 48px 16px;
  z-index: 1080;
  overflow-y: auto;
}
.ud-modal {
  background: var(--g-surface, #fff);
  border-radius: var(--g-radius, 12px);
  width: 100%;
  max-width: 720px;
  box-shadow: var(--g-shadow-3);
  overflow: hidden;
}
.ud-modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid var(--g-border);
}
.ud-modal__title {
  margin: 0;
  font-size: 15.5px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ud-modal__title i { color: var(--g-blue); }
.ud-close {
  background: none;
  border: none;
  font-size: 1.15rem;
  color: var(--g-text-secondary);
  cursor: pointer;
  line-height: 1;
  padding: 4px 8px;
  border-radius: var(--g-radius-sm);
}
.ud-close:hover { background: var(--g-hover); color: var(--g-text); }
.ud-modal__body { padding: 16px 18px 20px; }
.ud-empty { padding: 20px; text-align: center; }
.ud-empty-inline { padding: 8px 0; }

.ud-identity {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--g-divider);
}
.ud-avatar {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--g-blue);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.15rem;
}
.ud-name { font-size: 15.5px; font-weight: 700; color: var(--g-text); }
.ud-sub { font-size: 13px; color: var(--g-text-secondary); margin-bottom: 6px; }
.ud-badges { display: flex; flex-wrap: wrap; gap: 6px; }
.ud-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  text-transform: capitalize;
}
.ud-badge--green { background: #e6f4ea; color: #137333; }
.ud-badge--red { background: #fce8e6; color: #c5221f; }
.ud-badge--blue { background: #e8f0fe; color: #1a73e8; }
.ud-badge--purple { background: #f3e8fd; color: #8430ce; }
.ud-badge--amber { background: #fef7e0; color: #b06000; }
.ud-badge--grey { background: var(--g-surface-2); color: var(--g-text-secondary); }

.ud-section { margin-top: 16px; }
.ud-section__title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--g-text-secondary);
  margin-bottom: 10px;
}
.ud-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px 24px;
}
.ud-field { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.ud-field--full { grid-column: 1 / -1; }
.ud-label { font-size: 0.72rem; color: var(--g-text-secondary); text-transform: uppercase; letter-spacing: 0.03em; }
.ud-value { font-size: 14px; color: var(--g-text); word-break: break-word; }
.ud-value a { color: var(--g-blue); }
.ud-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.82rem; }
.ud-verified { color: #137333; margin-left: 6px; font-size: 0.8rem; }

/* Message Info -> Message column. Content is injected with v-html, so the
   generated <a> carries no scope attribute — :deep() is required to style it. */
.msg-cell { min-width: 260px; max-width: 460px; white-space: normal; word-break: break-word; }
.msg-cell :deep(a) { color: var(--g-blue); text-decoration: none; font-weight: 500; }
.msg-cell :deep(a:hover) { text-decoration: underline; }

</style>
